import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Tabs, { TABS_VARIANT_TEXT } from '@components/2-molecules/Tabs';
import useAuth from '@contexts/useAuth';
import { SETTING_TABS } from '@constants/settingTabs';
import { useFeatureGate, FeatureGate } from '@featureFlags/index';
import { TabsContainer } from './SettingsTabs.styled';

interface SettingsTabsProps {
  activeTab: string;
}

const SettingsTabs: React.FC<SettingsTabsProps> = ({ activeTab }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isOrgAdmin =
    user?.orgRole === 'AIBUILD_ADMIN' || user?.orgRole === 'ORG_ADMIN';
  const customToolsEnabled = useFeatureGate(FeatureGate.CUSTOM_TOOL_WORKBENCH);

  const tabs = useMemo(() => {
    const allTabs = Object.entries(SETTING_TABS);
    return allTabs
      .filter(([key]) => {
        if (key === 'BILLING' && !isOrgAdmin) return false;
        if (key === 'TOOLS' && (!isOrgAdmin || !customToolsEnabled)) return false;
        return true;
      })
      .map(([, tab]) => ({
        ...tab,
        active: activeTab === tab.title,
      }));
  }, [isOrgAdmin, customToolsEnabled, activeTab]);

  const containerRef = useRef<HTMLDivElement>(null);
  const activeTabId = tabs.find((tab) => tab.active)?.id;

  useEffect(() => {
    const container = containerRef.current;
    const activeTab = container?.querySelector<HTMLElement>(
      `[data-testid="settings-page__tabs__tab-${activeTabId}"]`,
    );
    if (!container || !activeTab) return;
    container.scrollLeft =
      activeTab.offsetLeft - (container.clientWidth - activeTab.offsetWidth) / 2;
  }, [activeTabId]);

  const handleTabClick = useCallback(
    (tab: { link?: string }) => {
      if (tab?.link) navigate(tab.link);
    },
    [navigate],
  );

  return (
    <TabsContainer ref={containerRef}>
      <Tabs
        dataTestId="settings-page__tabs"
        variant={TABS_VARIANT_TEXT}
        tabs={tabs}
        onTabClick={handleTabClick}
      />
    </TabsContainer>
  );
};

export default SettingsTabs;
